import { motion } from "framer-motion";
import { Phone, MessageCircle, ShieldCheck, Star, MapPin } from "lucide-react";
import { BRAND, telHref, waHref, LOGO_URL } from "@/lib/brand";

const heroBg =
    "https://images.pexels.com/photos/4876639/pexels-photo-4876639.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

export default function Hero() {
    return (
        <section
            id="top"
            data-testid="hero-section"
            className="relative min-h-screen w-full flex items-end pt-28 pb-16 overflow-hidden bg-[#fafbfc]"
        >
            {/* Background image with light overlay */}
            <div className="absolute inset-0">
                <img
                    src={heroBg}
                    alt="Pressure washing in action"
                    className="w-full h-full object-cover opacity-20"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/30" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#fafbfc] via-transparent to-white/70" />
                {/* Grain */}
                <div
                    className="absolute inset-0 opacity-[0.08] pointer-events-none"
                    style={{
                        backgroundImage:
                            "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>')",
                    }}
                />
            </div>

            {/* Decorative logo on the right (large faded) */}
            <div className="hidden lg:block absolute right-[-80px] top-1/2 -translate-y-1/2 pointer-events-none">
                <img
                    src={LOGO_URL}
                    alt=""
                    className="w-[560px] h-[560px] object-contain opacity-90 drop-shadow-[0_30px_60px_rgba(30,136,229,0.25)]"
                />
            </div>

            {/* Vertical side label */}
            <div className="hidden lg:block absolute left-6 top-1/2 -translate-y-1/2 -rotate-90 origin-left text-[10px] tracking-[0.6em] text-black/30 font-semibold">
                EST. GEORGIA · SAFE HANDS · PEACE OF MIND
            </div>

            <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 w-full">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="inline-flex items-center gap-2 border border-black/10 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-sm mb-8 shadow-sm"
                    data-testid="hero-badge"
                >
                    <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-[#1E88E5] opacity-75 animate-ping" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#1E88E5]" />
                    </span>
                    <span className="text-xs uppercase tracking-widest text-gray-700">
                        Call Now · Free Quotes · No Pressure
                    </span>
                </motion.div>

                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.1 }}
                    className="font-display font-bold uppercase text-5xl sm:text-7xl lg:text-8xl leading-[0.9] tracking-tight max-w-5xl text-[#0a0a0a]"
                    data-testid="hero-headline"
                >
                    <span className="text-[#1E88E5]">Stronger</span> Solutions
                    <br />
                    Services.
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.3 }}
                    className="mt-6 max-w-xl text-base sm:text-lg text-gray-600 leading-relaxed"
                    data-testid="hero-subtitle"
                >
                    Powerful pressure washing, window cleaning, and commercial
                    services across Georgia. We blast away years of dirt, mold
                    and grime — leaving your property looking brand new.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.45 }}
                    className="mt-10 flex flex-col sm:flex-row gap-3"
                >
                    <a
                        href={telHref}
                        data-testid="hero-cta-call"
                        className="group inline-flex items-center justify-center gap-3 bg-[#1E88E5] hover:bg-[#1976D2] text-white font-bold uppercase tracking-wider px-7 py-4 rounded-sm transition-all pulse-blue"
                    >
                        <Phone className="w-5 h-5" />
                        Call {BRAND.phoneDisplay}
                    </a>
                    <a
                        href={waHref}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="hero-cta-whatsapp"
                        className="inline-flex items-center justify-center gap-3 bg-white border border-black/15 hover:border-green-500 hover:bg-green-50 text-[#0a0a0a] font-bold uppercase tracking-wider px-7 py-4 rounded-sm transition-all"
                    >
                        <MessageCircle className="w-5 h-5 text-green-600" />
                        WhatsApp Quote
                    </a>
                </motion.div>

                {/* Trust strip */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.7 }}
                    className="mt-14 grid grid-cols-2 md:flex md:items-center md:gap-10 gap-6"
                    data-testid="hero-trust-strip"
                >
                    <div className="flex items-center gap-2">
                        <ShieldCheck className="w-5 h-5 text-[#1E88E5]" />
                        <div className="text-xs">
                            <div className="font-bold uppercase tracking-wider text-[#0a0a0a]">
                                Safe Hands
                            </div>
                            <div className="text-gray-500">Peace of Mind</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Star className="w-5 h-5 text-[#1E88E5] fill-[#1E88E5]" />
                        <div className="text-xs">
                            <div className="font-bold uppercase tracking-wider text-[#0a0a0a]">
                                5.0 Rating
                            </div>
                            <div className="text-gray-500">
                                240+ Happy Clients
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-[#1E88E5]" />
                        <div className="text-xs">
                            <div className="font-bold uppercase tracking-wider text-[#0a0a0a]">
                                Serving Georgia
                            </div>
                            <div className="text-gray-500">
                                & Surrounding Areas
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
